import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Snackbar,
  SnackbarCloseReason,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core"
import { Alert, Autocomplete, Color } from "@material-ui/lab"
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date"
import { useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import dayjs, { Dayjs } from "dayjs"
import DOMPurify from "dompurify"
import React, { SyntheticEvent, useCallback, useEffect } from "react"
import { useParams } from "react-router-dom"
import { useRecoilState } from "recoil"
import {
  ClientFragment,
  Enum_Insight_Content_Type_Enum,
  GetQuestionnaireScheduleNotifyAllDocument,
  GetQuestionnaireScheduleNotifyAllQuery,
  GetQuestionnaireScheduleNotifyAllQueryVariables,
  Maybe,
  NudgeFragment,
  QuestionnaireFragment,
  useEditScheduledNudgeMutation,
  useEditScheduledQuestionnaireMutation,
  useGetAllActiveNudgesQuery,
  useGetQuestionnairesQuery,
  useScheduleNudgeMutation,
  useScheduleQuestionnaireMutation,
} from "../../generated/graphql"
import { useClients } from "../../hooks/useClients"
import { useGetResourceById } from "../../hooks/useGetResourceById"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useInAppResources } from "../../hooks/useInAppResources"
import { useSession } from "../../hooks/useSession"
import { useUser } from "../../hooks/useUser"
import { NudgeDomain, Query, Role, nudgeOrder } from "../../utils/constants"
import { combineDateTime } from "../../utils/dateTimeUtils"
import {
  DAYS_OF_WEEK,
  addHttp,
  isNudge,
  isNudgeSchedule,
  isScheduleItem,
} from "../../utils/helpers"
import { enableScheduledItemTabState } from "../../utils/store"
import { colors } from "../../utils/theme"
import {
  InAppResourcesTableData,
  InitDaysValue,
  PickerTab,
  ScheduleItem,
  ScheduleMutationVariables,
  ScheduleTab,
  UnscheduledItem,
} from "../../utils/types"
import CloseIcon from "../atoms/CloseIcon"
import TriangleExclamationIcon from "../atoms/TriangleExclamationIcon"
import Button, { GhostButton } from "../atoms/button/Button"
import { Tabs } from "../molecules/Tabs"
import MobileUpdateDialog from "./MobileUpdateDialog"
import MobileVerifyDialog from "./MobileVerifyDialog"
import { ScheduleDateTimeInputs } from "./ScheduleDateTimeInputs"
import ScheduleExpirationTimeInputs from "./ScheduleExpirationTimeInputs"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"

const useStyles = makeStyles((theme) => ({
  hyperlink: {
    color: colors.primary.periwinkle,
    textDecoration: "underline",
    cursor: "pointer",
  },
  closeButton: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: theme.spacing(2),
  },
  title: {
    padding: theme.spacing(4, 4, 2),
  },
  contentRoot: {
    padding: theme.spacing(1, 4, 4),
  },
  tabButton: {
    backgroundColor: colors.primary.ice_03,
    opacity: 1,
    color: colors.primary.midnight,
    textTransform: "capitalize",
    minHeight: 50,
    "&.Mui-selected": {
      backgroundColor: colors.primary.ice,
    },
    "&:first-child": {
      borderTopLeftRadius: 100,
      borderBottomLeftRadius: 100,
    },
    "&:last-child": {
      borderTopRightRadius: 100,
      borderBottomRightRadius: 100,
    },
    "&.MuiTab-root": {
      minWidth: "unset",
    },
  },
  tabPanel: {
    display: "grid",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
  },
  domainLabel: {
    display: "flex",
    justifyContent: "center",
  },
  domainDot: {
    height: 10,
    width: 10,
    margin: 8,
    borderRadius: 20,
    display: "inline-block",
  },
  noCursor: {
    caretColor: "transparent",
    cursor: "pointer",
  },
  warningContainer: {
    display: "flex",
  },
  warningIcon: {
    marginRight: theme.spacing(2),
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  warningText: {
    color: colors.neutral.denim,
  },
  warningTextMargin: {
    marginBottom: theme.spacing(1),
  },
  phoneButton: {
    display: "inline-block",
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  dialogSubtitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
  confirmButton: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  cancelContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  },
  closeButtonDisconnected: {
    margin: 0,
    padding: 0,
    border: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
}))

interface ScheduleDialogProps extends DialogProps {
  item?: ScheduleItem | UnscheduledItem
  startOnQuestionnaireTab?: boolean
  canSchedule?: boolean
  client: ClientFragment
}

export const ScheduleDialog: React.FC<ScheduleDialogProps> = ({
  item,
  startOnQuestionnaireTab,
  canSchedule,
  client,
  open,
  onClose,
  ...props
}) => {
  const classes = useStyles()
  const { user } = useUser()
  const { session } = useSession()
  const userId = user.id ?? ""
  const { id: clientId } = useParams<{ id: string }>()
  const graphqlClient = useGraphQLClient()
  const nudges = useGetAllActiveNudgesQuery(graphqlClient).data?.nudges
  const questionnaires =
    useGetQuestionnairesQuery(graphqlClient).data?.questionnaires
  const [scheduleTab, setScheduleTab] = React.useState(ScheduleTab.NUDGE)
  const [domain, setDomain] = React.useState<NudgeDomain | null>(null)
  const [resourceId, setResourceId] = React.useState<Maybe<number> | undefined>(
    undefined
  )
  const [containRiskAlert, setContainRiskAlert] = React.useState<boolean>(false)
  const [hasPhoneNumber, setHasPhoneNumber] = React.useState<boolean>(true)
  const [openMobileUpdateDialog, setOpenMobileUpdateDialog] =
    React.useState<boolean>(false)
  const [openMobileVerifyDialog, setOpenMobileVerifyDialog] =
    React.useState<boolean>(false)
  const [mobile, setMobile] = React.useState<string>("")
  const [expiration, setExpiration] = React.useState<number>(1440)
  const [, setInAppResource] = React.useState<InAppResourcesTableData | null>(
    null
  )
  const [message, setMessage] = React.useState("")
  const { inAppResources } = useInAppResources(user.organizations?.[0].id ?? -1)
  const [technique, setTechnique] = React.useState<NudgeFragment | null>(null)
  const [techniques, setTechniques] = React.useState<NudgeFragment[] | null>()
  const [notifyAllPractitioners, setNotifyAllPractitioners] =
    React.useState(false)
  const [questionnaire, setQuestionnaire] =
    React.useState<QuestionnaireFragment | null>(null)
  const [enableScheduledItemTab, setEnableScheduledItemTab] = useRecoilState(
    enableScheduledItemTabState
  )
  const resource = useGetResourceById(resourceId ?? undefined)
  const queryClient = useQueryClient()
  const onSuccess = (data: any) => {
    if (
      data?.update_questionnaire_schedules_by_pk === null ||
      data?.update_nudge_schedules_by_pk === null
    ) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Oops! Something went wrong. Please try again later.",
      })
      return
    }
    queryClient.invalidateQueries({ queryKey: ["NudgesScheduleByUser"] })
    queryClient.invalidateQueries({
      queryKey: ["QuestionnairesScheduleByUser"],
    })
    queryClient.invalidateQueries({ queryKey: [Query.GET_IN_APP_RESOURCES] })
  }

  const onError = () => {
    setSnackbar({
      open: true,
      severity: "error",
      message: "Oops! Something went wrong. Please try again later.",
    })
  }
  const { mutate: scheduleNudge } = useScheduleNudgeMutation(graphqlClient, {
    onSuccess,
    onError,
  })
  const { mutate: scheduleQuestionnaire, error } =
    useScheduleQuestionnaireMutation(graphqlClient, {
      onSuccess,
      onError,
    })

  const { mutate: editNudge } = useEditScheduledNudgeMutation(graphqlClient, {
    onSuccess,
    onError,
  })
  const { mutate: editQuestionnaire } = useEditScheduledQuestionnaireMutation(
    graphqlClient,
    {
      onSuccess,
      onError,
    }
  )

  const { showBriteFeatures } = useOrganizationFeatureFlags()
  const { clients } = useClients()

  const clientFirstName = clients?.find((c) => c.id.data === clientId)
    ?.firstName.data

  /**
   * Set contain risk alert and notify all practitioners when item is set if it's a questionnaire schedule
   */
  useEffect(() => {
    // confirm this is a questionnaireSchedule
    if (item && "questionnaire" in item) {
      setContainRiskAlert(item.questionnaire.contains_risk_questions ?? false)
      graphqlClient
        .request<
          GetQuestionnaireScheduleNotifyAllQuery,
          GetQuestionnaireScheduleNotifyAllQueryVariables
        >(GetQuestionnaireScheduleNotifyAllDocument, { id: item.id })
        .then((res) => {
          setNotifyAllPractitioners(
            res.questionnaire_schedules_by_pk?.notify_connected_practitioners ??
              false
          )
        })
    }
  }, [graphqlClient, item])

  const filterBrite = useCallback(
    (nudges: NudgeFragment[] | undefined) => {
      if (!nudges) return []
      return nudges.filter(
        (nudge) => showBriteFeatures || nudge.technique !== "Emotional Cooldown"
      )
    },
    [showBriteFeatures]
  )

  const getDomains = (): NudgeDomain[] => {
    const filteredNudges = filterBrite(nudges)
    const domains = Array.from(new Set(filteredNudges?.map((n) => n.domain)))
    const intersectionDomains = nudgeOrder.filter((n) => domains.includes(n))
    return intersectionDomains
  }

  const getQuestionnaires = () => {
    const questions = questionnaires
    return questions ?? []
  }

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })

  const openMobileDialog = () => {
    setOpenMobileUpdateDialog(true)
  }

  const handleCloseSnackbar = (
    event: SyntheticEvent<any, Event>,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  const save = async () => {
    const now = dayjs()
    try {
      if (!user) {
        throw new Error("User not set")
      }
      if (!selectedTime) {
        throw new Error("Please set Notification Time")
      }
      if (pickerTab === PickerTab.ONE_TIME && !selectedDate) {
        throw new Error("Pick a one time date")
      }
      if (
        pickerTab === PickerTab.ONE_TIME &&
        selectedDate &&
        dayjs(selectedDate).isToday() &&
        dayjs(selectedTime).isBefore(dayjs())
      ) {
        throw new Error("One Time date and time must be in the future")
      }
      if (pickerTab === PickerTab.RECURRING && !days.find((d) => d.selected)) {
        throw new Error("Pick at least one recurring day")
      }
      type Days = Partial<Record<InitDaysValue, boolean>>
      const recurringDays = days.reduce((all: Days, curr) => {
        all[curr.value] =
          pickerTab === PickerTab.RECURRING ? curr.selected : false
        return all
      }, {})
      let scheduled_at
      if (pickerTab === PickerTab.ONE_TIME && selectedDate) {
        scheduled_at = combineDateTime(selectedDate, selectedTime)
      } else {
        // Recurring schedules: pick the next selected weekday/time that is >= now.
        const timeOfDay = dayjs(selectedTime)
        const selectedWeekdays = days
          .map((d, index) => ({ index, selected: d.selected }))
          .filter((d) => d.selected)
          .map((d) => d.index)

        const candidates = selectedWeekdays.map((weekday) => {
          const offsetDays = (weekday - now.day() + 7) % 7 // next weekday is x days away
          let candidate = now
            .add(offsetDays, "day")
            .hour(timeOfDay.hour())
            .minute(timeOfDay.minute())
            .second(0)
            .millisecond(0)

          // If it's the same weekday but earlier than 'now', push to next week.
          if (candidate.isBefore(now)) {
            candidate = candidate.add(7, "day")
          }
          return candidate
        })

        scheduled_at = candidates.reduce((earliest, curr) =>
          curr.isBefore(earliest) ? curr : earliest
        )
      }

      const timePayload: Pick<
        ScheduleMutationVariables,
        "one_time" | "scheduled_at"
      > &
        Days = {
        one_time: pickerTab === PickerTab.ONE_TIME,
        scheduled_at: scheduled_at
          ? scheduled_at.format("YYYY-MM-DDTHH:mm")
          : "",
        ...recurringDays,
      }

      if (scheduleTab === ScheduleTab.NUDGE) {
        if (!domain) {
          throw new Error("No domain chosen")
        }
        if (!technique) {
          throw new Error("No technique chosen")
        }
        if (message === "") {
          throw new Error("Include a message to continue")
        }

        if (item && isScheduleItem(item)) {
          editNudge({
            id: item.id,
            nudge_id: technique.id,
            message: message,
            ...timePayload,
          })
        } else {
          scheduleNudge({
            nudge_id: technique.id,
            practitioner_id: user.id,
            user_id: clientId,
            message: message,
            ...timePayload,
          })
        }
      } else {
        if (!questionnaire) {
          throw new Error("No questionnaire chosen")
        }
        if (expiration < 0) {
          throw new Error("Expiration time is in the past.")
        } else if (expiration <= 1) {
          throw new Error("Expiration time must be greater than 1 minute")
        }
        // 28 days in minutes
        if (expiration > 40320) {
          throw new Error("Expiration time cannot be greater than 28 days")
        }
        // if the questionnaire contains risk questions, and the user has selected to notify all practitioners, get the current timestamp to put in the notify_connected_practitioners field
        let sendToAllTimestamp =
          containRiskAlert && notifyAllPractitioners ? dayjs.utc() : null
        if (item && isScheduleItem(item)) {
          editQuestionnaire({
            id: item.id,
            questionnaire_id: questionnaire.id,
            minutes_to_expiration: expiration,
            notify_connected_practitioners: sendToAllTimestamp,
            ...timePayload,
          })
        } else {
          scheduleQuestionnaire({
            questionnaire_id: questionnaire.id,
            practitioner_id: user.id,
            user_id: clientId,
            minutes_to_expiration: expiration,
            notify_connected_practitioners: sendToAllTimestamp,
            ...timePayload,
          })
        }
      }
      setSnackbar({
        open: session?.role !== Role.WEB_ROLE, // don't show if the user role is web_role (i.e. a supervisor)
        severity: "success",
        message: "Scheduled!",
      })
      setEnableScheduledItemTab(true)

      // in order to fire the onClose, it needs garbage arguments
      onClose?.({} as never, "backdropClick")
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: (error as unknown as { message: string }).message,
      })
      console.error("schedule error", error)
    }
  }

  // Date Picker component and state
  const [pickerTab, setPickerTab] = React.useState(PickerTab.ONE_TIME)
  const [selectedDate, handleDateChange] = React.useState<
    Dayjs | MaterialUiPickersDate
  >(dayjs())
  const [selectedTime, handleTimeChange] = React.useState<
    Dayjs | MaterialUiPickersDate
  >(dayjs())

  interface InitDaysProps {
    value: InitDaysValue
    label: DAYS_OF_WEEK
    selected: boolean
  }
  const initDays: InitDaysProps[] = React.useMemo(
    () => [
      {
        value: "recurring_sunday",
        label: DAYS_OF_WEEK.SUNDAY,
        selected: false,
      },
      {
        value: "recurring_monday",
        label: DAYS_OF_WEEK.MONDAY,
        selected: false,
      },
      {
        value: "recurring_tuesday",
        label: DAYS_OF_WEEK.TUESDAY,
        selected: false,
      },
      {
        value: "recurring_wednesday",
        label: DAYS_OF_WEEK.WEDNESDAY,
        selected: false,
      },
      {
        value: "recurring_thursday",
        label: DAYS_OF_WEEK.THURSDAY,
        selected: false,
      },
      {
        value: "recurring_friday",
        label: DAYS_OF_WEEK.FRIDAY,
        selected: false,
      },
      {
        value: "recurring_saturday",
        label: DAYS_OF_WEEK.SATURDAY,
        selected: false,
      },
    ],
    []
  )
  const [days, setDays] = React.useState(initDays)

  // pre-fill with item
  React.useEffect(() => {
    if (!item) {
      return
    }
    if (isScheduleItem(item)) {
      setPickerTab(item.one_time ? PickerTab.ONE_TIME : PickerTab.RECURRING)
      handleTimeChange(dayjs(item.scheduled_at))
      handleDateChange(dayjs(item.scheduled_at))
      setDays(initDays.map((d) => ({ ...d, selected: item[d.value] })))
      if (isNudgeSchedule(item)) {
        setDomain(nudgeOrder.find((n) => n === item.nudge.domain) ?? null)
        setTechnique(item.nudge)
        setMessage(item.message)
        const resources = inAppResources
        const theResouce = resources.filter(
          (x) => x.id.data === item?.nudge.resource_id
        )
        setInAppResource(theResouce[0])
        setScheduleTab(ScheduleTab.NUDGE)
      } else {
        setQuestionnaire(item.questionnaire)
        setScheduleTab(ScheduleTab.ASSESSMENT)
      }
    } else {
      if (isNudge(item)) {
        setDomain(nudgeOrder.find((n) => n === item.domain) ?? null)
        setTechnique(item)
        const resources = inAppResources
        const theResouce = resources.filter(
          (x) => x.id.data === item?.resource_id
        )
        setInAppResource(theResouce[0])
        setResourceId(item?.resource_id)
        const message = autoFillName(item.default_messaging)
        setMessage(message)
        setScheduleTab(ScheduleTab.NUDGE)
      } else {
        setQuestionnaire(item)
        setContainRiskAlert(item?.contains_risk_questions ?? false)
        setHasPhoneNumber(user.phoneNumber ? true : false)
        setScheduleTab(ScheduleTab.ASSESSMENT)
      }
    }
  }, [item, initDays, inAppResources, user.phoneNumber])

  React.useEffect(() => {
    const currentTechniques = nudges?.filter((n) => n.domain === domain)
    setTechniques(currentTechniques)
    if (!technique) {
      setMessage("")
    }
  }, [domain, nudges, technique])

  React.useEffect(() => {
    if (!open) {
      // clear state on close
      // TODO: use initial tab
      setScheduleTab(
        startOnQuestionnaireTab ? ScheduleTab.ASSESSMENT : ScheduleTab.NUDGE
      )
      setDomain(null)
      setMessage("")
      setTechnique(null)
      setTechniques(null)
      setQuestionnaire(null)
      // Picker state
      setPickerTab(PickerTab.ONE_TIME)
      handleDateChange(null)
      handleTimeChange(null)
      setDays([...initDays])
      setContainRiskAlert(false)
      setHasPhoneNumber(true)
    }
  }, [open, initDays, startOnQuestionnaireTab])

  /**
   * Set notify all practitioners when radio button is changed
   * @param event the event
   */
  const handleNotifyPractitionersChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNotifyAllPractitioners(event.target.value === "1")
  }

  const autoFillName = (message: string) => {
    return message.replace(/\[FIRST NAME\]/g, clientFirstName ?? "")
  }

  useEffect(() => {
    if (open && canSchedule === false) {
      onClose?.({} as never, "backdropClick")
    }
  }, [canSchedule, onClose, open])

  return (
    <>
      {open && canSchedule !== false ? (
        <>
          <Dialog
            {...props}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
          >
            {/* Sort of a hack, but I have to give the `onClose` arguments to work :shrug: */}
            <IconButton
              className={classes.closeButton}
              onClick={(e) => {
                setEnableScheduledItemTab(true)
                onClose?.(e, "backdropClick")
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogTitle disableTypography className={classes.title}>
              <Typography variant="h3" component="h2">
                Schedule Item
              </Typography>
            </DialogTitle>
            <DialogContent className={classes.contentRoot}>
              <Tabs
                ariaLabel="Nudges and Assessment tabs"
                tabs={[
                  { label: ScheduleTab.NUDGE, className: classes.tabButton },
                  {
                    label: ScheduleTab.ASSESSMENT,
                    className: classes.tabButton,
                  },
                ]}
                onChange={(tab) => {
                  setScheduleTab(tab)
                }}
                initialTab={scheduleTab}
                enabled={enableScheduledItemTab}
              >
                {/* Nudge Tab */}
                <div className={classes.tabPanel}>
                  <Autocomplete
                    id="domain-name"
                    options={getDomains()}
                    value={domain}
                    onChange={(_, value) => {
                      if (value !== domain) {
                        setTechnique(null)
                      }
                      setDomain(value)
                    }}
                    autoSelect
                    autoComplete
                    autoHighlight
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Domain Name"
                        variant="outlined"
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment
                              position="start"
                              style={{ margin: 0 }}
                            >
                              <div
                                className={classes.domainDot}
                                style={{
                                  backgroundColor: domain
                                    ? colors.schedule[domain].border
                                    : "",
                                }}
                              />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(domainOption) => (
                      <span className={classes.domainLabel}>
                        <div
                          className={classes.domainDot}
                          style={{
                            backgroundColor: domainOption
                              ? colors.schedule[domainOption].border
                              : "",
                          }}
                        />
                        {domainOption}
                      </span>
                    )}
                  />
                  <Autocomplete
                    id="technique-name"
                    disabled={!domain}
                    value={technique ?? null}
                    options={techniques ?? []}
                    getOptionSelected={(option, value) =>
                      option.id === value.id
                    }
                    getOptionLabel={(option) => option.technique ?? ""}
                    onChange={(_, value) => {
                      setTechnique(value)
                      setMessage(value?.default_messaging ?? "")
                      const resources = inAppResources
                      const theResouce = resources.filter(
                        (x) => x.id.data === value?.resource_id
                      )
                      setInAppResource(theResouce[0])
                      setResourceId(value?.resource_id)
                    }}
                    autoSelect
                    autoHighlight
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Technique Name"
                        variant="outlined"
                      />
                    )}
                  />
                  <ScheduleDateTimeInputs
                    pickerTab={pickerTab}
                    setPickerTab={setPickerTab}
                    selectedDate={selectedDate}
                    handleDateChange={handleDateChange}
                    selectedTime={selectedTime}
                    handleTimeChange={handleTimeChange}
                    days={days}
                    setDays={setDays}
                    client={client}
                  />
                  {/* <Autocomplete
                id="inAppResource"
                onClose={() => console.log("closed")}
                options={inAppResources.filter(
                  (x) => x.resource_type.data === "article"
                )}
                getOptionLabel={(option) => option.title.data ?? ""}
                getOptionSelected={(option, value) =>
                  option.id.data === value.id.data
                }
                value={inAppResource ?? null}
                onChange={(event, value, reason) => {
                  if (reason === "clear") {
                    setResourceId(-1)
                    setDeleteResource(true)
                    return
                  }
                  setInAppResource(value ?? null)
                }}
                autoSelect
                autoHighlight
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Resource" variant="outlined" />
                )}
              /> */}
                  {resource ? (
                    <TextField
                      hidden={true}
                      value={
                        resource ? "View Resource" : "No resource attached"
                      }
                      onClick={() => {
                        if (!resource) return
                        if (
                          resource?.content_type ===
                          Enum_Insight_Content_Type_Enum.Link
                        ) {
                          window.open(addHttp(resource.content))
                        } else {
                          const win = window.open("", "Preview")
                          if (win) {
                            win.document.body.innerHTML = DOMPurify.sanitize(
                              resource?.content ?? ""
                            )
                          }
                        }
                      }}
                      label="Resource"
                      contentEditable={false}
                      variant="outlined"
                      disabled={!resource}
                      fullWidth
                      inputProps={{
                        className: clsx(classes.hyperlink, classes.noCursor),
                      }}
                    />
                  ) : null}
                  <TextField
                    value={message ?? ""}
                    onChange={(e) => setMessage(e.target.value)}
                    label="Message"
                    variant="outlined"
                    fullWidth
                  />
                </div>
                {/* Questionnaire Tab */}
                <div className={classes.tabPanel}>
                  <Autocomplete
                    id="questionnaire"
                    options={getQuestionnaires()}
                    getOptionLabel={(option) => option.name}
                    value={questionnaire}
                    onChange={(_, value) => {
                      setQuestionnaire(value ?? null)
                      setContainRiskAlert(
                        value?.contains_risk_questions ?? false
                      )
                      setHasPhoneNumber(user.phoneNumber ? true : false)
                    }}
                    autoSelect
                    autoHighlight
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assessment"
                        variant="outlined"
                      />
                    )}
                  />
                  {containRiskAlert && (
                    <div className={classes.warningContainer}>
                      <div className={classes.warningIcon}>
                        <TriangleExclamationIcon />
                      </div>
                      <div>
                        <Typography
                          variant="body1"
                          className={clsx(
                            classes.warningText,
                            classes.warningTextMargin
                          )}
                        >
                          This assessment includes high risk questions. A risk
                          notification may be triggered based on your client's
                          responses.
                        </Typography>
                        {!hasPhoneNumber && (
                          <Typography
                            variant="body1"
                            className={clsx(
                              classes.warningText,
                              classes.warningTextMargin
                            )}
                          >
                            When a risk alert is triggered, you will receive an
                            email notification.{" "}
                            <div className={classes.phoneButton}>
                              <GhostButton onClick={openMobileDialog}>
                                Click here
                              </GhostButton>
                            </div>{" "}
                            to enter a mobile number for text notifications.
                          </Typography>
                        )}
                        <Typography
                          variant="body1"
                          className={classes.warningText}
                        >
                          Notify the following practitioners:
                        </Typography>
                        <RadioGroup
                          aria-label="notifyPractitionersRadioGroup"
                          name="notify-radio-group"
                          value={notifyAllPractitioners ? "1" : "0"}
                          onChange={handleNotifyPractitionersChange}
                        >
                          <FormControlLabel
                            value="0"
                            control={<Radio />}
                            label="Only me"
                          />
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label="All connected practitioners"
                          />
                        </RadioGroup>
                      </div>
                    </div>
                  )}
                  <ScheduleDateTimeInputs
                    client={client}
                    pickerTab={pickerTab}
                    setPickerTab={setPickerTab}
                    selectedDate={selectedDate}
                    handleDateChange={handleDateChange}
                    selectedTime={selectedTime}
                    handleTimeChange={handleTimeChange}
                    days={days}
                    setDays={setDays}
                  />
                </div>
              </Tabs>
              {scheduleTab === ScheduleTab.ASSESSMENT && questionnaire?.id && (
                <ScheduleExpirationTimeInputs
                  pickerTab={pickerTab}
                  updateExpiration={setExpiration}
                  questionnaireId={questionnaire!.id}
                  questionnaireScheduleId={item?.id}
                  scheduledDate={selectedDate}
                  scheduledTime={selectedTime}
                />
              )}
              <Button onClick={save} customVariant="primary" fullWidth>
                Save
              </Button>
            </DialogContent>
          </Dialog>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
              {snackbar.message}
            </Alert>
          </Snackbar>
          <MobileVerifyDialog
            open={openMobileVerifyDialog}
            mobile={mobile}
            userId={userId}
            openMobileUpdateDialog={() => {
              setOpenMobileVerifyDialog(false)
              setOpenMobileUpdateDialog(true)
            }}
            verifySuccess={() => {
              setOpenMobileVerifyDialog(false)
              setHasPhoneNumber(true)
            }}
            closeCallback={() => {
              setOpenMobileVerifyDialog(false)
            }}
          />
          <MobileUpdateDialog
            open={openMobileUpdateDialog}
            mobile={mobile}
            openMobileVerifyDialog={(updateMobile: string) => {
              setOpenMobileUpdateDialog(false)
              if (updateMobile) {
                setMobile(updateMobile)
                setOpenMobileVerifyDialog(true)
              }
            }}
            closeCallback={() => {
              setOpenMobileUpdateDialog(false)
            }}
          />
        </>
      ) : null}
    </>
  )
}
